import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, getUserDetail, suspendUser, unsuspendUser, wipeUser } from '../lib/api';
import { Badge, Button, Card, ErrorBox, Label, Loading, PageHeader, TextInput, formatDate } from '../components/ui';

export function UserDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['user', id], queryFn: () => getUserDetail(id) });

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox message={(q.error as Error).message} />;
  const u = q.data!;

  const refetch = () => qc.invalidateQueries({ queryKey: ['user', id] });

  return (
    <>
      <PageHeader
        title={u.displayName}
        subtitle={
          u.telegramUsername ? `@${u.telegramUsername} · ${u.telegramId}` : `tg ${u.telegramId}`
        }
        right={
          u.isSuspended ? (
            <Badge tone="danger">Suspended until {formatDate(u.suspendedUntil)}</Badge>
          ) : (
            <Badge tone="success">Active</Badge>
          )
        }
      />

      <section className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card>
          <p className="text-xs text-text-soft">Driver mode</p>
          <p className="mt-1 font-semibold">{u.isDriver ? 'On' : 'Off'}</p>
        </Card>
        <Card>
          <p className="text-xs text-text-soft">Rides completed</p>
          <p className="mt-1 font-semibold">{u.ridesCompleted}</p>
        </Card>
        <Card>
          <p className="text-xs text-text-soft">Rating</p>
          <p className="mt-1 font-semibold">
            {u.ratingAvg !== null ? `${u.ratingAvg.toFixed(2)} (${u.ratingCount})` : '—'}
          </p>
        </Card>
        <Card>
          <p className="text-xs text-text-soft">Open reports</p>
          <p className="mt-1 font-semibold">{u.reportCountOpen}</p>
        </Card>
      </section>

      <Card className="mb-4">
        <h2 className="mb-3 text-sm font-semibold text-brand-navy">Related data</h2>
        <ul className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
          <li>Rides: <span className="font-semibold">{u.counts.rides}</span></li>
          <li>Bookings (passenger): <span className="font-semibold">{u.counts.bookingsAsPassenger}</span></li>
          <li>Bookings (driver): <span className="font-semibold">{u.counts.bookingsAsDriver}</span></li>
          <li>Ratings given: <span className="font-semibold">{u.counts.ratingsGiven}</span></li>
          <li>Ratings received: <span className="font-semibold">{u.counts.ratingsReceived}</span></li>
          <li>Reports: <span className="font-semibold">{u.counts.reports}</span></li>
          <li>Notifications: <span className="font-semibold">{u.counts.notifications}</span></li>
          <li>Created: <span className="font-semibold">{formatDate(u.createdAt)}</span></li>
        </ul>
      </Card>

      <SuspendBlock user={u} onChange={refetch} />
      <WipeBlock userId={u.id} displayName={u.displayName} onWiped={() => navigate('/users')} />
    </>
  );
}

function SuspendBlock({
  user,
  onChange,
}: {
  user: { id: string; isSuspended: boolean };
  onChange: () => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const [days, setDays] = useState(7);

  const suspendMut = useMutation({
    mutationFn: () =>
      suspendUser(user.id, {
        untilMs: days > 0 ? Date.now() + days * 24 * 3600 * 1000 : null,
        reason,
      }),
    onSuccess: () => {
      setReason('');
      onChange();
    },
  });
  const unsuspendMut = useMutation({
    mutationFn: () => unsuspendUser(user.id),
    onSuccess: onChange,
  });

  return (
    <Card className="mb-4">
      <h2 className="mb-3 text-sm font-semibold text-brand-navy">Suspension</h2>
      {user.isSuspended ? (
        <Button variant="secondary" onClick={() => unsuspendMut.mutate()} disabled={unsuspendMut.isPending}>
          {unsuspendMut.isPending ? 'Unsuspending…' : 'Unsuspend'}
        </Button>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[120px_1fr_auto]">
          <div>
            <Label htmlFor="days">Duration (days, 0 = forever)</Label>
            <TextInput
              id="days"
              type="number"
              min={0}
              max={365}
              value={String(days)}
              onChange={(e) => setDays(Number(e.target.value))}
            />
          </div>
          <div>
            <Label htmlFor="reason">Reason</Label>
            <TextInput
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Required for audit log"
            />
          </div>
          <div className="self-end">
            <Button
              variant="danger"
              onClick={() => suspendMut.mutate()}
              disabled={!reason.trim() || suspendMut.isPending}
            >
              {suspendMut.isPending ? 'Suspending…' : 'Suspend'}
            </Button>
          </div>
        </div>
      )}
      {suspendMut.isError && <ErrorBox message={(suspendMut.error as Error).message} />}
      {unsuspendMut.isError && <ErrorBox message={(unsuspendMut.error as Error).message} />}
    </Card>
  );
}

function WipeBlock({
  userId,
  displayName,
  onWiped,
}: {
  userId: string;
  displayName: string;
  onWiped: () => void;
}): JSX.Element {
  const [confirm, setConfirm] = useState('');
  const mut = useMutation({
    mutationFn: () => wipeUser(userId, confirm),
    onSuccess: onWiped,
  });

  return (
    <Card>
      <h2 className="mb-2 text-sm font-semibold text-destructive">Delete user + all data</h2>
      <p className="mb-3 text-xs text-text-soft">
        Irreversible. Cascades through bookings, rides, ratings, reports, notifications, templates,
        referrals, blocks, premium, anti-abuse flags, and audit entries by this actor. The user can
        re-register from scratch on next Telegram open.
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
        <div>
          <Label htmlFor="confirm">
            Type <code className="rounded bg-bg px-1">DELETE {displayName}</code> to confirm
          </Label>
          <TextInput
            id="confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={`DELETE ${displayName}`}
          />
        </div>
        <div className="self-end">
          <Button
            variant="danger"
            onClick={() => mut.mutate()}
            disabled={confirm !== `DELETE ${displayName}` || mut.isPending}
          >
            {mut.isPending ? 'Deleting…' : 'Delete forever'}
          </Button>
        </div>
      </div>
      {mut.isError && (
        <div className="mt-3">
          <ErrorBox
            message={
              mut.error instanceof ApiError
                ? `${mut.error.code}: ${mut.error.message}`
                : (mut.error as Error).message
            }
          />
        </div>
      )}
    </Card>
  );
}
